<script lang="ts" setup>
  import { ref, watch } from 'vue'
  import type { Ref } from 'vue'

  import { useFirebaseStorage } from 'vuefire'

  import { getBlob, ref as storageRef } from 'firebase/storage'

  import { useAppStore } from '#ourahub-intra/stores/application'

  import type { Service } from '#ourahub-intra/types/services'

  const props = defineProps<{
    services: Service[]
  }>()

  const ouraHubStore = useAppStore()

  const fbStorage = useFirebaseStorage()

  const showUpdates = ref(false)

  const versions: Record<string, string> = {}

  const newServices: Ref<Service[]> = ref([])

  const updatedServices: Ref<Service[]> = ref([])

  watch(
    () => [props.services, ouraHubStore.isLoading, ouraHubStore.serviceVersions],
    async () => {
      if (!props.services || ouraHubStore.isLoading || !ouraHubStore.serviceVersions) {
        return
      }

      console.log('Checking for updates...', props.services.length)

      newServices.value = []
      updatedServices.value = []

      for (const service of props.services) {
        const latestVersion = service.version

        if (!service.public || !latestVersion || latestVersion === ouraHubStore.serviceVersions?.[service.id]) {
          continue
        }

        const changelog = await getBlob(storageRef(fbStorage, `changelogs/${service.id}.md`))
          .then((blob) => blob?.text())
          .catch(() => null)

        if (changelog) {
          versions[service.id] = latestVersion

          if (ouraHubStore.serviceVersions?.[service.id]) {
            updatedServices.value.push(service)
          } else {
            newServices.value.push(service)
          }
        }
      }

      showUpdates.value = newServices.value.length > 0 || updatedServices.value.length > 0
    },
    { immediate: true },
  )

  async function storeVersions() {
    await ouraHubStore.updateServiceVersions(versions)

    showUpdates.value = false
  }

  async function filterServices() {
    showUpdates.value = false

    ouraHubStore.searchOrTask = 'New and updated apps / tools'

    ouraHubStore.searchResults = newServices.value.concat(updatedServices.value)

    await storeVersions()
  }
</script>

<template lang="html">
  <div class="box">
    <v-alert
      tile
      closable
      class="alert"
      position="absolute"
      color="blue-lighten-1"
      icon="mdi-bell-outline"
      style="cursor: pointer"
      :class="showUpdates ? 'visible' : ''"
      @click="filterServices()"
    >
      <template #close>
        <o-btn-text color="info" icon="mdi-close-thick" @click.stop="storeVersions()" />
      </template>

      <template v-if="newServices.length && updatedServices.length">
        There {{ newServices.length > 1 ? 'are' : 'is' }} {{ newServices.length }} newly released and
        {{ updatedServices.length }} recently updated {{ updatedServices.length > 1 ? 'apps / tools' : 'app / tool' }},
      </template>
      <template v-else-if="newServices.length > 1">
        There are {{ newServices.length }} newly released apps / tools,
      </template>
      <template v-else-if="updatedServices.length > 1">
        There are {{ updatedServices.length }} recently updated apps / tools,
      </template>
      <template v-else-if="newServices.length">
        There is a new release of
        <b>{{ newServices[0]!.name }}</b>
        available,
      </template>
      <template v-else-if="updatedServices.length">
        There was a recent update for
        <b>{{ updatedServices[0]!.name }}</b>
        available,
      </template>
      click here to see whats new!
    </v-alert>
  </div>
</template>

<style lang="scss" scoped>
  .box {
    position: sticky;
    top: 0;
    z-index: 10;
    margin-bottom: 60px;

    .alert {
      top: -120px;
      transition: top 1s ease-in-out;
      width: 100%;

      &.visible {
        top: 0;
      }
    }
  }
</style>
